import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Checkbox } from '@src/common/components/Checkbox';
import { IconButton } from '@src/common/components/IconButton';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@src/common/components/List';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { ViewableDocumentShortcut } from '@src/common/components/ViewableDocumentShortcut';
import { DownloadService } from '@src/common/services/DownloadService';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { ConditionsSheetsClient } from '@src/orders/services/ConditionsSheetsClient';
import type { FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import {
  ConditionSheetsField,
  SendToClientFieldName,
} from '@src/permits/fields/Fields';
import { Download01 } from '@untitled-ui/icons-react';
import type {
  Control,
  FieldArrayWithId,
  FieldValues,
  Path,
  PathValue,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type WithSheets = FieldValues & {
  conditionsSheets?: FormConditionSheet[];
};

interface Props<T extends WithSheets> {
  control: Control<T>;
  conditionsSheets: T extends WithSheets
    ? FieldArrayWithId<WithSheets, 'conditionsSheets', 'id'>[]
    : never;
  readOnly?: boolean;
}

export const ConditionsSheetsSelection = <T extends WithSheets>({
  control,
  conditionsSheets,
  readOnly,
}: Props<T>) => {
  const { t } = useTranslation();

  if (conditionsSheets.length === 0) return null;

  return (
    <Stack>
      <Stack
        direction="column"
        gap={theme.spacing['3']}
        marginTop={theme.spacing['4']}
      >
        <Typography className={accordionTitle}>
          {t('conditionsSheets')}
        </Typography>
        <Stack overflow="auto" maxHeight={400}>
          <List>
            {conditionsSheets.map((item, index) => (
              <ConditionsSheetItem
                key={item.id}
                index={index}
                file={item}
                control={control}
                readOnly={readOnly}
              />
            ))}
          </List>
        </Stack>
      </Stack>
    </Stack>
  );
};

const download = async (fileId: string) => {
  const { blob, filename } = await ConditionsSheetsClient.download(fileId);
  void DownloadService.download(blob, filename);
};

const ConditionsSheetItem = <T extends WithSheets>({
  file,
  index,
  control,
  readOnly,
}: {
  file: FieldArrayWithId<WithSheets, 'conditionsSheets', 'id'>;
  index: number;
  control: Control<T>;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();

  const downloadConditionsSheet = () => {
    void download(file.id);
  };

  const componentId = `conditions-sheets-${file.id}`;
  const { user } = useAuthentication();

  return (
    <ListItem className={item} aria-labelledby={componentId}>
      <ListItemIcon>
        <Stack direction="row">
          {!isClientUser(user) && (
            <Controller
              name={
                `${ConditionSheetsField.fieldName}.${index}.${SendToClientFieldName}` as Path<T>
              }
              control={control}
              defaultValue={file.sendToClient as PathValue<T, Path<T>>}
              render={({ field: { value, ...field } }) => (
                <Checkbox
                  {...field}
                  checked={value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                  hideLabel
                  label={t('sendToClient')}
                  disabled={readOnly}
                />
              )}
            />
          )}
          <ViewableDocumentShortcut
            document={{ id: file.id, key: file.filePath }}
            fontSize="large"
            download={ConditionsSheetsClient.download}
          />
        </Stack>
      </ListItemIcon>
      <ListItemText
        id={componentId}
        inset
        primary={file.filePath}
        primaryTypographyProps={{
          sx: { fontWeight: 500 },
        }}
      />

      <IconButton title={t('download')} onClick={downloadConditionsSheet}>
        <Download01 />
      </IconButton>
    </ListItem>
  );
};

const item = css(() => ({
  margin: '9px 0px',
}));

const accordionTitle = css(() => ({
  fontWeight: 500,
  borderBottom: '1px solid',
}));
